











































































































































import {
  defineComponent,
  PropType,
  useContext,
  computed,
  ref,
  onMounted,
  watch
} from '@nuxtjs/composition-api';
import { createSmoothScroll } from '@wemade-vsf/core';
import { useWmAttributes } from '@wemade-vsf/catalog/lib/runtime/product/composables/useWmAttributes';
import { SfHeading } from '@storefront-ui/vue'
import CmsContent from 'components/theme/Content/CmsContent.vue';
import ProductAttachment from 'components/catalog/Product/ProductAttachment.vue';
import ProductAttributeGroup from 'components/catalog/Product/ProductAttributeGroup.vue';
import ProductTab from 'components/catalog/Product/ProductTab.vue';
import type { Product, ProductAttachmentInterface } from '@wemade-vsf/catalog/lib/runtime/product/types';
import type { ComplexTextValue } from '@wemade-vsf/magento-api';

const mobileBreakpoint = 767;

interface MenuItem {
  label: string;
  id: string;
  visible: boolean;
}

export default defineComponent({
  name: 'ProductTabs',
  props: {
    product: {
      type: [Object, null] as PropType<Product | null>
    },
    attachments: {
      type: [Array, null] as PropType<ProductAttachmentInterface[] | null>
    }
  },
  components: {
    SfHeading,
    CmsContent,
    ProductAttachment,
    ProductAttributeGroup,
    ProductTab,
    ProductLinksSlider: () => import('components/catalog/Product/ProductLinksSlider.vue')
  },
  setup (props) {
    const { app: { i18n } } = useContext();

    const { attributeGroups, productAttributes, specificationExcludeAtts } = useWmAttributes()

    const upsellProducts = computed<string[] | null>(() => props.product?.upsell_products?.length
      ? props.product.upsell_products.map(p => p?.sku || '').filter(Boolean) : null)
    const relatedProducts = computed<string[] | null>(() => props.product?.related_products?.length
      ? props.product.related_products.map(p => p?.sku || '').filter(Boolean) : null)
    const crosssellProducts = computed<string[] | null>(() => props.product?.crosssell_products?.length
      ? props.product.crosssell_products.map(p => p?.sku || '').filter(Boolean) : null)
    const serviceProducts = computed<string[] | null>(() => props.product?.service_products?.length
      ? props.product.service_products.map(p => p?.sku || '').filter(Boolean) : null)
    const productVideo = computed(() => productAttributes.value['wm_video'] ? productAttributes.value['wm_video']['value'] : null);
   
    const menuItems = computed<MenuItem[]>(() => {
      return [
        { label: i18n.t('Product description'), id: 'description', visible: true },
        { label: i18n.t('Product specifications'), id: 'specifications', visible: true },
        { label: i18n.t('Documents'), id: 'attachments', visible: !!(props.attachments && props.attachments.length) },
        { label: i18n.t('Video'), id: 'product-video', visible: !!productVideo.value },
        { label: i18n.t('Accessories'), id: 'crosssell-products', visible: crosssellProducts.value?.length ? true : false },
        { label: i18n.t('Related products'), id: 'related-products', visible: relatedProducts.value?.length ? true : false },
        { label: i18n.t('Recommended'), id: 'upsell-products',  visible: upsellProducts.value?.length ? true : false },
        { label: i18n.t('Spare parts'), id: 'service-products', visible: serviceProducts.value?.length ? true : false }
      ]
    })

    const isMobile = ref(false);
    let resizeObserver: ResizeObserver | null = null;
    if (process.browser && ('ResizeObserver' in window)) {
      resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          isMobile.value = entry.contentRect.width <= mobileBreakpoint;
        }
      });
    }

    const supportsIntersection = computed(() => process.browser && ('IntersectionObserver' in window));

    const isSticky = ref<boolean>(false);
    const navigationObs = ref<HTMLElement | null>(null);
    const navigation = ref<HTMLElement | null>(null);
    let navigationObserver: IntersectionObserver | null = null;
    if (process.browser && ('IntersectionObserver' in window)) {
      navigationObserver = new IntersectionObserver((entries) => {
        if (isMobile.value) return;
        for (let entry of entries) {
          if (entry.intersectionRatio < 1 && entry.boundingClientRect.top < 300) {
            isSticky.value = true;
            initSectionObserver();
          } else {
            isSticky.value = false;
            disableSectionObserver();
          }

        }
      }, { threshold: [1] })
    }

    function initNavigationObserver() {
      if (supportsIntersection.value && navigationObserver && navigationObs.value) {
        navigationObserver.observe(navigationObs.value)
      }
    }
    function disableNavigationObserver() {
      if (navigationObserver && navigationObs.value) {
        navigationObserver.unobserve(navigationObs.value);
        isSticky.value = false;
      }
    }


    /**
     * Tabs and sections
     */
    const activeTab = ref<string | null>('description');
    const sections = ref<HTMLElement | null>(null);
    const loadSections = ref<string[]>([]);
    let sectionObserver: IntersectionObserver | null = null;
    function initSectionObserver() {
      if (supportsIntersection && sections.value) {
        if (!sectionObserver) {
          sectionObserver = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
              if (entry.isIntersecting) {
                activeTab.value = entry.target.id;
                if (!loadSections.value.includes(entry.target.id)) {
                  loadSections.value.push(entry.target.id);
                }
              }
            })
          }, { rootMargin: '-45% 0px -50% 0px', threshold: [0, 0.5, 1] });
        }
        for (let section of sections.value.children) {
          sectionObserver.observe(section);
        }
      }
    }
    function disableSectionObserver() {
      if (sectionObserver && sections.value) {
        for (let section of sections.value.children) {
          sectionObserver.unobserve(section);
        }
      }
    }

    const productDescription = computed<ComplexTextValue>(
      () => {
        if (props.product?.configurable_product_options_selection?.variant?.description) {
          return props.product?.configurable_product_options_selection?.variant?.description
        }
        return props.product?.description || { html: '', styles: null};
      }
    );
    const filteredAttributeGroups = computed(() => {
      if (!attributeGroups.value || attributeGroups.value.length === 0) {
        return null
      }
      const groups = attributeGroups.value
      return groups.map(g => {
        let attributes = g.attributes || []
        attributes = attributes.filter(a => {
          return !!a.value && a.is_visible_on_front && !specificationExcludeAtts.includes(a.attribute_code)
        })
        return {
          ...g,
          attributes
        }
      }).filter(g => g.attributes.length)
    })

    function setActiveTab(id: string | null) {
      activeTab.value = id;
      if (id && !loadSections.value.includes(id)) {
        loadSections.value.push(id);
      }
      if (id && !isMobile.value && sections.value) {
        const tab = document.getElementById(id);
        const top = tab?.offsetTop ?? 0;
        
        const navHeight = navigation.value ? navigation.value.clientHeight : 52;
        createSmoothScroll(document.documentElement.scrollTop || document.body.scrollTop, top - navHeight - 20);
      }
    }

    /**
     * lifecycle
     */
    onMounted(() => {
      if (!process.browser) return;
      isMobile.value = document.body.clientWidth <= mobileBreakpoint;

      if (resizeObserver) {
        resizeObserver.observe(document.body);
      }

      if (!isMobile.value) {
        initNavigationObserver();
      }
    });

    watch(() => isMobile.value, (newValue) => {
      if (newValue) {
        disableNavigationObserver();
      } else {
        initNavigationObserver();
      }
    });

    return {
      isMobile,
      activeTab,
      navigationObs,
      navigation,
      isSticky,
      menuItems,
      sections,
      loadSections,
      productDescription,
      filteredAttributeGroups,
      setActiveTab,
      serviceProducts,
      crosssellProducts,
      upsellProducts,
      relatedProducts,
      productVideo
    }
  }
})
